import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import PostCard from './components/PostCard';
import CreatePostModal from './components/CreatePostModal';

const CommunityFeed = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading, userProfile } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const postTypes = [
    { id: 'all', label: 'All Posts', icon: 'Grid' },
    { id: 'workout', label: 'Workouts', icon: 'Dumbbell' },
    { id: 'achievement', label: 'Achievements', icon: 'Award' },
    { id: 'milestone', label: 'Milestones', icon: 'Trophy' },
    { id: 'photo', label: 'Photos', icon: 'Image' }
  ];

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadPosts();
      subscribeToPostUpdates();
    }
  }, [user, currentTenant, filterType]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      let query = supabase
        ?.from('posts')
        ?.select(`
          *,
          user_profiles!posts_user_id_fkey(id, full_name, avatar_url),
          reactions(id, user_id, reaction_type),
          comments(id, user_id, content, created_at, user_profiles(full_name, avatar_url))
        `)
        ?.eq('tenant_id', currentTenant?.id);

      if (filterType !== 'all') {
        query = query?.eq('post_type', filterType);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      setPosts(data || []);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToPostUpdates = () => {
    const channel = supabase
      ?.channel('posts-channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'posts',
          filter: `tenant_id=eq.${currentTenant?.id}`
        },
        () => {
          loadPosts();
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const handleCreatePost = async (postData) => {
    try {
      const { error } = await supabase
        ?.from('posts')
        ?.insert([{
          tenant_id: currentTenant?.id,
          user_id: user?.id,
          ...postData
        }]);

      if (error) throw error;

      setShowCreateModal(false);
      loadPosts();
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleReaction = async (postId, reactionType) => {
    try {
      // Check if user already reacted
      const { data: existing } = await supabase
        ?.from('reactions')
        ?.select('id')
        ?.eq('post_id', postId)
        ?.eq('user_id', user?.id)
        ?.single();

      if (existing) {
        // Remove reaction
        await supabase?.from('reactions')?.delete()?.eq('id', existing?.id);
      } else {
        // Add reaction
        await supabase?.from('reactions')?.insert([{
          post_id: postId,
          user_id: user?.id,
          reaction_type: reactionType
        }]);
      }

      loadPosts();
    } catch (error) {
      console.error('Error handling reaction:', error);
    }
  };

  const handleComment = async (postId, content) => {
    try {
      const { error } = await supabase
        ?.from('comments')
        ?.insert([{
          post_id: postId,
          user_id: user?.id,
          content
        }]);

      if (error) throw error;

      loadPosts();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading community feed...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="mx-auto max-w-[1200px] px-4 md:px-6 py-6 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Community Feed</h1>
              <p className="text-muted-foreground">Share your fitness journey and connect with members</p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2"
            >
              <Icon name="Plus" size={20} />
              <span className="hidden md:inline">Create Post</span>
            </Button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {postTypes?.map((type) => (
              <button
                key={type?.id}
                onClick={() => setFilterType(type?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-smooth ${
                  filterType === type?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:bg-accent'
                }`}
              >
                <Icon name={type?.icon} size={18} />
                <span className="text-sm font-medium">{type?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Posts Feed */}
        <div className="space-y-4">
          {posts?.length === 0 ? (
            <div className="bg-card rounded-xl shadow-md p-12 text-center">
              <Icon name="Users" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No posts yet</h3>
              <p className="text-muted-foreground mb-6">
                Be the first to share your fitness journey with the community!
              </p>
              <Button onClick={() => setShowCreateModal(true)}>
                <Icon name="Plus" size={20} className="mr-2" />
                Create First Post
              </Button>
            </div>
          ) : (
            posts?.map((post) => (
              <PostCard
                key={post?.id}
                post={post}
                currentUserId={user?.id}
                onReaction={handleReaction}
                onComment={handleComment}
              />
            ))
          )}
        </div>
      </main>

      {/* Create Post Modal */}
      {showCreateModal && (
        <CreatePostModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreatePost}
        />
      )}

      <MobileBottomNav />
    </div>
  );
};

export default CommunityFeed;